import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  FiMail, FiPhone, FiMapPin, FiGithub, FiLinkedin,
  FiSend, FiCheckCircle, FiLoader, FiAlertCircle, FiCheck,
} from 'react-icons/fi';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { sendEmail, buildVisitorEmail, buildOwnerEmail, OWNER_EMAIL } from '../services/emailService';

// ─── Purpose Options (same as chatbot) ───────────────────────────────────────
const PURPOSE_OPTIONS = [
  { id: 'visit',    label: 'Visit / Explore Portfolio' },
  { id: 'hire',     label: 'Hire Full-time / Contract' },
  { id: 'career',   label: 'Career / Internship Opportunity' },
  { id: 'freelance',label: 'Freelance / Consulting Project' },
  { id: 'app_dev',  label: 'App Development (Mobile / Web)' },
  { id: 'project',  label: 'Other Project Collaboration' },
];

// ─── Contact link cards ───────────────────────────────────────────────────────
const contactLinks = [
  { icon: FiMail,     label: 'Email',    value: 'kallesh953@gmail.com',             href: 'mailto:kallesh953@gmail.com',                          color: 'primary' },
  { icon: FiPhone,    label: 'Phone',    value: '+91 7676 654 809',                 href: 'tel:+917676654809',                                    color: 'accent'  },
  { icon: FiMapPin,   label: 'Location', value: 'Bengaluru, Karnataka, India',      href: 'https://maps.google.com/?q=Bengaluru,India',           color: 'blue'    },
  { icon: FiLinkedin, label: 'LinkedIn', value: 'linkedin.com/in/kallesh-dr',       href: 'https://www.linkedin.com/in/kallesh-dr-087008235',     color: 'sky'     },
  { icon: FiGithub,   label: 'GitHub',   value: 'github.com/kalleshdr',             href: 'https://github.com/kalleshdr',                        color: 'violet'  },
];

const colorMap = {
  primary: { text: 'text-primary-600', icon: 'bg-primary-100', border: 'border-primary-100' },
  accent:  { text: 'text-accent-600',  icon: 'bg-accent-100',  border: 'border-accent-100'  },
  blue:    { text: 'text-blue-600',    icon: 'bg-blue-100',    border: 'border-blue-100'    },
  sky:     { text: 'text-sky-600',     icon: 'bg-sky-100',     border: 'border-sky-100'     },
  violet:  { text: 'text-violet-600',  icon: 'bg-violet-100',  border: 'border-violet-100'  },
};

const inputCls =
  'w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-all bg-white';
const labelCls = 'text-xs font-semibold text-slate-600 mb-1.5 block';

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Contact() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  const [form, setForm] = useState({
    name: '',
    email: '',
    purpose: [],
    subject: '',
    message: '',
  });
  const [errors, setErrors]       = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Toggle multi-select purpose
  const togglePurpose = (id) => {
    setForm((f) => ({
      ...f,
      purpose: f.purpose.includes(id)
        ? f.purpose.filter((p) => p !== id)
        : [...f.purpose, id],
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim())                                    e.name    = 'Name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
    if (form.purpose.length === 0)                            e.purpose = 'Select at least one purpose';
    if (!form.subject.trim())                                 e.subject = 'Subject is required';
    if (!form.message.trim())                                 e.message = 'Message is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setSubmitError('');

    const purposeLabels = form.purpose.map(
      (id) => PURPOSE_OPTIONS.find((o) => o.id === id)?.label || id
    );
    const timestamp = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });

    try {
      // 1 — Save to Firestore
      await addDoc(collection(db, 'contact_messages'), {
        ...form,
        purposeLabels,
        timestamp,
        source: 'portfolio_contact_section',
        createdAt: serverTimestamp(),
      });

      // 2 — Confirmation email to visitor
      await sendEmail({
        to: form.email,
        subject: `Thank you for reaching out, ${form.name}! — Kallesh D R`,
        text: buildVisitorEmail({ ...form, purpose: purposeLabels }),
      });

      // 3 — Notification email to Kallesh
      await sendEmail({
        to: OWNER_EMAIL,
        subject: `New Contact Message: ${form.name} — ${purposeLabels.join(', ')}`,
        text: buildOwnerEmail({ ...form, purpose: purposeLabels, timestamp }),
      });

      setSubmitted(true);
    } catch (err) {
      console.error('Contact form error:', err);
      setSubmitError('Something went wrong. Please email kallesh953@gmail.com directly.');
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-slate-50">
      <div className="section-container">

        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-1 h-8 bg-gradient-to-b from-primary-600 to-accent-500 rounded-full" />
            <p className="text-primary-600 font-semibold text-sm uppercase tracking-widest">Contact</p>
          </div>
          <h2 className="section-title">Get In Touch</h2>
          <p className="text-slate-400 text-sm">Open to full-time roles, consulting engagements, and collaborations.</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10">

          {/* ── Left: Contact Info ── */}
          <div className="space-y-3">
            {contactLinks.map((item, i) => {
              const cfg = colorMap[item.color];
              const Icon = item.icon;
              return (
                <motion.a
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  initial={{ opacity: 0, x: -20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                  className={`flex items-center gap-4 card p-4 border ${cfg.border} hover:border-current group transition-all duration-200`}
                >
                  <div className={`w-11 h-11 rounded-xl ${cfg.icon} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-200`}>
                    <Icon size={20} className={cfg.text} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 font-medium">{item.label}</p>
                    <p className={`text-sm font-semibold ${cfg.text}`}>{item.value}</p>
                  </div>
                </motion.a>
              );
            })}

            {/* Availability badge */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.5 }}
              className="card p-5 bg-gradient-to-r from-accent-50 to-primary-50 border border-accent-100"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2.5 h-2.5 rounded-full bg-accent-500 animate-pulse" />
                <p className="text-sm font-bold text-slate-800">Currently Available</p>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Open to full-time SDE roles, consulting engagements, and exciting technical collaborations. Response time typically within 24 hours.
              </p>
            </motion.div>
          </div>

          {/* ── Right: Contact Form ── */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="card p-6"
          >
            {submitted ? (
              /* ── Success State ── */
              <div className="flex flex-col items-center justify-center py-12 gap-5 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200 }}
                  className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center"
                >
                  <FiCheckCircle size={32} className="text-accent-600" />
                </motion.div>
                <div>
                  <p className="font-bold text-slate-800 text-lg">Message Sent!</p>
                  <p className="text-slate-500 text-sm mt-1 leading-relaxed max-w-xs">
                    Your details are saved and a confirmation email is on its way to you. Kallesh will respond within <strong>24 hours</strong>.
                  </p>
                </div>
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100 text-left w-full">
                  <p className="text-xs font-semibold text-slate-600 mb-2">Direct Contacts</p>
                  <p className="text-xs text-slate-500">📧 kallesh953@gmail.com</p>
                  <p className="text-xs text-slate-500">📞 +91 7676654809</p>
                </div>
                <button
                  onClick={() => { setSubmitted(false); setForm({ name: '', email: '', purpose: [], subject: '', message: '' }); }}
                  className="btn-outline text-sm py-2.5 px-6"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              /* ── Form ── */
              <form onSubmit={handleSubmit} className="space-y-5" id="contact-form" noValidate>
                <h3 className="font-display font-bold text-slate-800 text-lg">Send a Message</h3>

                {/* Name + Email row */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} htmlFor="cf-contact-name">Your Name *</label>
                    <input
                      id="cf-contact-name"
                      name="name"
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="John Doe"
                      className={inputCls}
                    />
                    {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="cf-contact-email">Your Email *</label>
                    <input
                      id="cf-contact-email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="john@company.com"
                      className={inputCls}
                    />
                    {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                  </div>
                </div>

                {/* Purpose Multi-Select */}
                <div>
                  <label className={labelCls}>Purpose * <span className="font-normal text-slate-400">(select all that apply)</span></label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {PURPOSE_OPTIONS.map((opt) => {
                      const selected = form.purpose.includes(opt.id);
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => togglePurpose(opt.id)}
                          className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-xs font-medium transition-all duration-150 text-left ${
                            selected
                              ? 'bg-primary-600 border-primary-600 text-white shadow-sm'
                              : 'bg-white border-slate-200 text-slate-700 hover:border-primary-300 hover:bg-primary-50'
                          }`}
                        >
                          <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border transition-colors ${
                            selected ? 'bg-white border-white' : 'border-slate-300'
                          }`}>
                            {selected && <FiCheck size={10} className="text-primary-600" strokeWidth={3} />}
                          </div>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  {errors.purpose && <p className="text-xs text-red-500 mt-1">{errors.purpose}</p>}
                </div>

                {/* Subject */}
                <div>
                  <label className={labelCls} htmlFor="cf-contact-subject">Subject *</label>
                  <input
                    id="cf-contact-subject"
                    name="subject"
                    type="text"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="e.g. Senior SDE Role at Acme Corp"
                    className={inputCls}
                    autoComplete="off"
                  />
                  {errors.subject && <p className="text-xs text-red-500 mt-1">{errors.subject}</p>}
                </div>

                {/* Message */}
                <div>
                  <label className={labelCls} htmlFor="cf-contact-message">Message *</label>
                  <textarea
                    id="cf-contact-message"
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell me about the role, project, or idea you have in mind..."
                    className={`${inputCls} resize-none`}
                  />
                  {errors.message && <p className="text-xs text-red-500 mt-1">{errors.message}</p>}
                </div>

                {/* API error */}
                {submitError && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
                    <FiAlertCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-600">{submitError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  id="submit-contact"
                >
                  {submitting ? (
                    <>
                      <FiLoader size={15} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <FiSend size={15} />
                      Send Message
                    </>
                  )}
                </button>

                <p className="text-center text-xs text-slate-400">
                  You'll receive a confirmation email with all details. Response within 24 hours.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
